import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IconButton, Slide, Box, Typography } from '@mui/material';
import NavigationIcon from '@mui/icons-material/Navigation';

// Custom hook for compass tracking - using DeviceOrientation API for true compass heading
function useCompassTracking() {
  const [deviceOrientation, setDeviceOrientation] = useState(0);
  const [isCompassTracking, setIsCompassTracking] = useState(false);
  const orientationHandlerRef = useRef(null);
  const lastUpdateRef = useRef(0);

  const startCompassTracking = useCallback(async () => {
    try {
      // Request permission for device orientation
      if (typeof DeviceOrientationEvent !== 'undefined' && DeviceOrientationEvent.requestPermission) {
        const permission = await DeviceOrientationEvent.requestPermission();
        if (permission !== 'granted') {
          console.log('🧭 Device orientation permission denied');
          return;
        }
      }
      
      // Handle device orientation events
      const handleOrientation = (event) => {
        const now = Date.now();
        // Throttle updates to max 10fps (100ms intervals)
        if (now - lastUpdateRef.current > 100) {
          let compassHeading;
          
          // Use webkitCompassHeading if available (true compass heading)
          if (event.webkitCompassHeading !== undefined) {
            compassHeading = event.webkitCompassHeading;
            console.log('🧭 True compass heading:', compassHeading);
          } else if (typeof event.alpha === 'number') {
            // Fallback to alpha with proper calculation
            compassHeading = 360 - event.alpha;
            console.log('🧭 Relative heading (alpha):', compassHeading);
          } else {
            return; // No valid orientation data
          }
          
          setDeviceOrientation(compassHeading);
          lastUpdateRef.current = now;
        }
      };
      
      // Add event listener
      window.addEventListener('deviceorientation', handleOrientation);
      
      // Store reference for cleanup
      orientationHandlerRef.current = () => {
        window.removeEventListener('deviceorientation', handleOrientation);
      };
      
      setIsCompassTracking(true);
      console.log('🧭 Compass tracking started');
      
    } catch (error) {
      console.error('Compass tracking error:', error);
    }
  }, []);

  const stopCompassTracking = useCallback(() => {
    if (orientationHandlerRef.current) {
      orientationHandlerRef.current();
      orientationHandlerRef.current = null;
    }
    setIsCompassTracking(false);
    console.log('🧭 Compass tracking stopped');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (orientationHandlerRef.current) {
        orientationHandlerRef.current();
      }
    };
  }, []);

  return {
    deviceOrientation,
    isMotionTracking: isCompassTracking,
    startMotionTracking: startCompassTracking,
    stopMotionTracking: stopCompassTracking
  };
}

const MotionControlButton = ({ onMotionStateChange }) => {
  const compassState = useCompassTracking();
  const {
    deviceOrientation,
    isMotionTracking,
    startMotionTracking,
    stopMotionTracking
  } = compassState;

  // Notify parent component of compass state changes
  useEffect(() => {
    if (onMotionStateChange) {
      onMotionStateChange(compassState);
    }
  }, [compassState, onMotionStateChange]);

  return (
    <Slide direction="left" in={true} mountOnEnter unmountOnExit>
      {isMotionTracking ? (
        // Active state: Pill-shaped button with text
        <Box
          onClick={stopMotionTracking}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: '#f44336', // Red color
            color: 'white',
            padding: '8px 16px',
            borderRadius: '25px', // Pill shape
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
            '&:hover': {
              backgroundColor: '#d32f2f',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)',
              transform: 'translateY(-1px)'
            },
            '&:active': {
              transform: 'translateY(0px)',
              boxShadow: '0 2px 6px rgba(244, 67, 54, 0.3)'
            }
          }}
          title="Disable compass tracking"
        >
          <NavigationIcon sx={{ fontSize: 20 }} />
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 500,
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Deactivate
          </Typography>
        </Box>
      ) : (
        // Inactive state: Circular icon button
        <IconButton 
          onClick={startMotionTracking} 
          aria-label="enable compass tracking" 
          size="large"
          title="Enable compass tracking for map rotation"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }
          }}
        >
          <NavigationIcon />
        </IconButton>
      )}
    </Slide>
  );
};

export default MotionControlButton;
